import { AppContext } from '../../../context';
import {
    AvailableInterviewOptions,
    InterviewStatus,
    InterviewerAlreadyBookedError,
    MultiDayOption,
    SingleDayOption,
} from '../../../types';
import { Button } from '@goodtimeio/rain';
import { DateTime } from 'luxon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { faSpinner } from '@fortawesome/pro-light-svg-icons';
import { getTimezone } from '../../../utils/date-utils';
import { isMobile } from 'react-device-detect';
import { searchSuperdayApi } from '../../../api';
import { timeZoneList } from '../../../utils/timezones';
import InterviewOptionsPicker from '../../../components/InterviewOptions/InterviewOptionPicker';
import ListTimezones from '../../../components/ListTimezones';
import React, { ReactElement, useContext, useEffect, useRef, useState } from 'react';
import Wrapper from '../../../components/Wrapper';

const voidFunction = () => {
    return;
};

const SuperdayPickdaysPage: React.FC = () => {
    const { hash } = useParams<{ hash: string }>();
    const {
        meetIntro,
        selectedDays,
        selectedTimeSlots = [],
        addTimeSlot,
        addTimeSlots,
        addDays,
        addSuperdayTimezone,
        availableInterviewOptions,
        postConfirmSuperdayAvailability,
        translate,
        receiveSuperdayResults,
    } = useContext(AppContext);
    const [isScheduling, setIsScheduling] = useState<boolean>(false);
    const [isLoadingResults, setIsLoadingResults] = useState<boolean>(false);
    const [noResults, setNoResults] = useState<boolean>(false);
    const [selectedTimezone, setSelectedTimezone] = useState<string>(getTimezone(meetIntro?.data?.interview));
    const [startX, setStartX] = useState<number>(0);
    const [scrollLeft, setScrollLeft] = useState<number>(0);
    const isDownRef = useRef<boolean>(false);
    const setIsDownState = (isDown: boolean) => {
        isDownRef.current = isDown;
    };
    const logo = meetIntro?.data?.organization?.logoUrl;
    const primaryColor = meetIntro?.data?.meetCompanyColor ? meetIntro?.data?.meetCompanyColor : '#6400E0';
    let textColor = '#FFFFFF';
    if (meetIntro?.data?.meetFontColor) {
        textColor = meetIntro?.data?.meetFontColor;
    }
    const history = useHistory();

    const ref = useRef<HTMLDivElement>(null);
    const [availableOptions, setAvailableOptions] = useState<AvailableInterviewOptions>({
        singleDay: new Map<string, SingleDayOption[]>(),
        multiDay: new Map<number, MultiDayOption[]>(),
        timezone: '',
    });

    const [activeAvailableOptions, setActiveAvailableOptions] = useState<AvailableInterviewOptions>({
        singleDay: new Map<string, SingleDayOption[]>(),
        multiDay: new Map<number, MultiDayOption[]>(),
        timezone: '',
    });

    const handleReceiveSuperdayResults = async () => {
        setIsLoadingResults(true);
        const superdayResults = await searchSuperdayApi(hash);

        if (superdayResults && superdayResults.length) {
            receiveSuperdayResults(superdayResults);
        } else {
            setNoResults(true);
        }
        setIsLoadingResults(false);
    };

    useEffect(() => {
        handleReceiveSuperdayResults();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (timezone) {
            if (
                !meetIntro?.data?.interview.settings.isOnsite &&
                meetIntro?.data?.guest.settings.timeZone !== timezone
            ) {
                addSuperdayTimezone(timezone);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (availableInterviewOptions.singleDay.size) {
            const autoLoadDays = getAutoLoadDays(availableInterviewOptions);
            selectedDays?.forEach((day) => autoLoadDays.add(day));
            const autoLoadList = Array.from(autoLoadDays);
            addDays(autoLoadList);
        }

        if (availableInterviewOptions) {
            setAvailableOptions(availableInterviewOptions);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        availableInterviewOptions,
        activeAvailableOptions.timezone,
        meetIntro?.data?.interview?.settings?.allowGuestToSuggestTimes,
    ]);

    useEffect(() => {
        if (selectedDays?.length) {
            setActiveAvailableOptions(getActiveInterviewOptions(selectedDays));
        } else {
            setActiveAvailableOptions({
                singleDay: new Map<string, SingleDayOption[]>(),
                multiDay: new Map<number, MultiDayOption[]>(),
                timezone: '',
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDays?.length, availableOptions]);

    useEffect(() => {
        if (ref.current) {
            const slider = ref.current;
            slider.addEventListener('mousedown', (e) => {
                setIsDownState(true);
                if (slider?.offsetLeft !== undefined) {
                    setStartX(e.pageX - slider.offsetLeft);
                    setScrollLeft(slider.scrollLeft);
                    slider.classList.add('active');
                }
            });
            slider.addEventListener('mouseleave', () => {
                setIsDownState(false);
                slider.classList.remove('active');
            });
            slider.addEventListener('mouseup', () => {
                setIsDownState(false);
                slider.classList.remove('active');
            });

            slider.addEventListener('mousemove', (event) => {
                if (!isDownRef.current) {
                    return;
                }
                if (isDownRef.current) {
                    event.preventDefault();
                    const x = event.pageX - slider.offsetLeft;
                    const walk = (x - startX) * 3;
                    slider.scrollLeft = scrollLeft - walk;
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref.current, isDownRef.current]);

    const interviewIsPendingCandidateResponse =
        meetIntro &&
        !meetIntro?.isLoading &&
        meetIntro?.data?.interview.status === InterviewStatus.PENDING_CANDIDATE_RESPONSE;

    const backgroundImage = meetIntro?.data?.meetBackgroundUrlList[1] || meetIntro?.data?.meetBackgroundUrlList[0];
    const dynamicButtonStyle = !selectedTimeSlots.length
        ? {}
        : { background: primaryColor, border: primaryColor, color: textColor };

    if (
        (!meetIntro || !interviewIsPendingCandidateResponse) &&
        !meetIntro?.isLoading &&
        meetIntro?.data?.interview.status !== InterviewStatus.RESCHEDULE &&
        meetIntro?.data?.interview.status !== InterviewStatus.PENDING_REVIEW
    ) {
        return <Redirect to={`/s/intro/${hash}`} />;
    }

    if (meetIntro && !meetIntro.isLoading && meetIntro?.data?.isPastTimeWindow) {
        return <Redirect to={`/s/expired/${hash}`} />;
    }

    if (noResults) {
        return <Redirect to={`/s/no-result/${hash}`} />;
    }

    const getSingleDayPickers = () => {
        const singleDayPickers: ReactElement[] = [];
        activeAvailableOptions.singleDay.forEach((singleDayOptions, date) => {
            singleDayPickers.push(
                <InterviewOptionsPicker
                    singleDayInterviewOptions={singleDayOptions}
                    companyColor={primaryColor}
                    textColor={textColor}
                    selectedTimeIds={selectedTimeSlots}
                    setSelectedTimeIds={handleSelectTime}
                    setSelectedTimeCategory={handleSelectTimeCategory}
                    date={date}
                    timezone={selectedTimezone}
                    displaySuggestTimesSlots={voidFunction}
                    allowGuestToSuggestTimes={meetIntro?.data?.interview?.settings?.allowGuestToSuggestTimes}
                    allowToSuggestTimesFeatureFlag={false}
                    isAlgoLoading={false}
                />,
            );
        });
        return singleDayPickers;
    };

    const onSelectTimezone = (timezone: string) => {
        setSelectedTimezone(timezone);
        addSuperdayTimezone(timezone);
    };

    const handleSelectTime = (newSelectedTimeId: string) => {
        addTimeSlot(newSelectedTimeId);
    };

    const handleSelectTimeCategory = (day: string, timeCategory: string) => {
        const optionsByDay = availableOptions.singleDay.get(day);

        const result =
            optionsByDay
                ?.filter((currTime) => currTime.timeCategory.label === timeCategory)
                .map((currTime) => currTime.hash) || [];

        addTimeSlots(result);
    };

    const getActiveInterviewOptions = (newSelectedDays: string[]) => {
        const newActiveAvailableOptions: AvailableInterviewOptions = {
            singleDay: new Map<string, SingleDayOption[]>(),
            multiDay: new Map<number, MultiDayOption[]>(),
            timezone: selectedTimezone,
        };

        availableOptions.singleDay.forEach((singleDayOption, date: string) => {
            if (newSelectedDays.includes(date)) {
                newActiveAvailableOptions.singleDay.set(date, singleDayOption);
            } else if (selectedTimeSlots?.length) {
                const selectedTimeSlot = singleDayOption.find((singleDay) =>
                    selectedTimeSlots.includes(singleDay.hash),
                );
                if (selectedTimeSlot) {
                    addTimeSlot(selectedTimeSlot.hash);
                }
            }
        });

        const unsortedArray = [...newActiveAvailableOptions.singleDay];
        unsortedArray.sort(([key1], [key2]) => {
            const startTimeA = DateTime.fromJSDate(new Date(key1));
            const startTimeB = DateTime.fromJSDate(new Date(key2));
            if (startTimeA > startTimeB) {
                return 1;
            }
            return -1;
        });
        newActiveAvailableOptions.singleDay = new Map<string, SingleDayOption[]>(unsortedArray);
        return newActiveAvailableOptions;
    };

    const onSchedule = async () => {
        setIsScheduling(true);
        try {
            await postConfirmSuperdayAvailability(hash);
            setIsScheduling(false);
            history.push(`/confirm/${hash}`);
        } catch (e) {
            if (e instanceof InterviewerAlreadyBookedError) {
                setIsScheduling(false);
                history.push(`/time-already-booked/${hash}`);
            }
        }
    };

    return (
        <Wrapper
            loading={meetIntro?.isLoading || isLoadingResults}
            backgroundImage={backgroundImage}
            logo={logo}
            message={translate('intro:calculatingAndBuildingInterviewSchedules')}
        >
            <div className="scheduleContainer">
                <div className="titleOnsiteTimezone">
                    {meetIntro?.data?.interview.settings.isOnsite ? (
                        <>
                            <b> {translate('pickDays:onSiteInterview')} </b>
                        </>
                    ) : (
                        <></>
                    )}
                </div>
                <div className="listTimezonesContainer">
                    <ListTimezones
                        selectedValue={{
                            value: selectedTimezone,
                            label: '',
                        }}
                        onChange={(val) => onSelectTimezone(val)}
                        options={timeZoneList}
                        isDisabled={meetIntro?.data?.interview?.settings.isOnsite ?? false}
                    />
                </div>
                {selectedDays?.length ? (
                    <>
                        <div className="optionPickerContainer" ref={ref}>
                            <>
                                {getSingleDayPickers().map((element, index) => (
                                    <React.Fragment key={`single-day-interview-option-picker-${index}`}>
                                        {element}
                                    </React.Fragment>
                                ))}
                            </>
                        </div>
                        {selectedTimeSlots.length > 0 && !isMobile ? (
                            <div className="optionPickerContainerFooter">
                                <b>{translate('pickDays:lookingGood')}&nbsp;</b>
                                {translate('pickDays:forGreaterFlexibility')}
                            </div>
                        ) : null}

                        <div className="footerContainer">
                            <Button
                                disabled={!selectedTimeSlots.length || isScheduling}
                                onClick={onSchedule}
                                className={'scheduleBtn'}
                                style={dynamicButtonStyle}
                                hidden={isMobile && !selectedTimeSlots.length}
                            >
                                {isScheduling ? (
                                    <FontAwesomeIcon icon={faSpinner} spin />
                                ) : (
                                    translate('pickDays:scheduleInterview')
                                )}
                            </Button>
                        </div>
                    </>
                ) : null}
            </div>
        </Wrapper>
    );
};

const getAutoLoadDays = (availableInterviewOptions: AvailableInterviewOptions) => {
    const daysForAutoLoad = new Set<string>(Array.from(availableInterviewOptions?.singleDay)?.map(([day]) => day));
    return daysForAutoLoad;
};

export default SuperdayPickdaysPage;
